/* Gallery Ledger interactions: Shopify AJAX cart, reliable product variants, and predictive search. */
(() => {
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const cartDrawer = $('[data-cart-drawer]');
  const searchOverlay = $('[data-search-overlay]');
  const menuOverlay = $('[data-menu]');
  let previousFocus = null;
  let cartMutationInFlight = false;

  const currency = window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'GBP';
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(cents / 100);
  const updateCount = (count) => $$('[data-cart-count]').forEach((node) => { node.textContent = `(${count})`; });
  const escape = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const setMessage = (node, message = '') => { if (node) node.textContent = message; };
  const setOpen = (element, open) => {
    if (!element) return;
    element.classList.toggle('is-open', open);
    element.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      previousFocus = document.activeElement;
      const target = $('button,input,a', element);
      target?.focus();
    } else if (previousFocus instanceof HTMLElement) {
      previousFocus.focus();
    }
  };
  const responseData = async (response, fallbackMessage) => {
    let data = null;
    try { data = await response.json(); } catch (_) { /* Shopify can return an empty response body. */ }
    if (!response.ok) throw new Error(data?.description || data?.message || fallbackMessage);
    return data;
  };

  const cartStatus = () => $('[data-cart-status]');
  const checkoutButton = () => $('[data-cart-checkout]');
  const renderCart = (cart) => {
    const destination = $('[data-cart-lines]');
    if (!destination) return;
    updateCount(cart.item_count);
    const subtotal = $('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);
    const checkout = checkoutButton();
    if (checkout) checkout.disabled = !cart.items.length;
    if (!cart.items.length) {
      destination.innerHTML = '<p class="empty-bag">Your bag is empty.</p>';
      return;
    }
    destination.innerHTML = cart.items.map((item) => {
      const image = item.image ? `<img src="${escape(item.image)}" alt="">` : '';
      const lineUrl = escape(item.url || '#');
      const key = escape(item.key);
      return `<article class="cart-line">${image}<div class="cart-line-copy"><small>${escape(item.product_type || 'Object')}</small><a href="${lineUrl}"><strong>${escape(item.product_title)}</strong></a><p>${escape(item.variant_title || '')}</p><div class="quantity-control"><button type="button" data-cart-change="${key}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity">−</button><span aria-live="polite">${item.quantity}</span><button type="button" data-cart-change="${key}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div></div><b>${money(item.final_line_price)}</b><button type="button" class="remove-line" data-cart-change="${key}" data-quantity="0">Remove</button></article>`;
    }).join('');
  };
  const getCart = async () => {
    const response = await fetch(`${window.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    const cart = await responseData(response, 'Your bag could not be loaded. Please refresh and try again.');
    renderCart(cart);
    return cart;
  };
  const changeCart = async (key, quantity) => {
    const response = await fetch(`${window.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const cart = await responseData(response, 'Your bag could not be updated. Please try again.');
    renderCart(cart);
    return cart;
  };
  const addToCart = async (id, quantity = 1) => {
    const response = await fetch(`${window.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] })
    });
    await responseData(response, 'This item could not be added to your bag. Please try again.');
    const cart = await getCart();
    setOpen(cartDrawer, true);
    return cart;
  };
  const withCartMutation = async (operation, statusNode = cartStatus()) => {
    if (cartMutationInFlight) return null;
    cartMutationInFlight = true;
    cartDrawer?.setAttribute('aria-busy', 'true');
    try {
      const result = await operation();
      setMessage(cartStatus(), '');
      return result;
    } catch (error) {
      console.error(error);
      setMessage(statusNode, error.message || 'Your bag could not be updated. Please try again.');
      return null;
    } finally {
      cartMutationInFlight = false;
      cartDrawer?.removeAttribute('aria-busy');
    }
  };

  $$('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
    const cart = await withCartMutation(getCart);
    if (cart) setOpen(cartDrawer, true);
  }));
  $('[data-cart-close]')?.addEventListener('click', () => setOpen(cartDrawer, false));
  cartDrawer?.addEventListener('click', (event) => {
    if (event.target === cartDrawer) {
      setOpen(cartDrawer, false);
      return;
    }
    const control = event.target.closest('[data-cart-change]');
    if (!control || cartMutationInFlight) return;
    control.disabled = true;
    withCartMutation(() => changeCart(control.dataset.cartChange, Number(control.dataset.quantity))).finally(() => { control.disabled = false; });
  });
  $$('[data-quick-add]').forEach((button) => button.addEventListener('click', async () => {
    if (button.disabled || cartMutationInFlight) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    await withCartMutation(() => addToCart(button.dataset.variantId));
    button.disabled = false;
    button.textContent = label;
  }));

  $$('form.product-form').forEach((form) => {
    const variantsPayload = $('[data-product-variants]', form);
    const selectedVariantInput = $('[data-variant-id]', form);
    const addButton = $('[data-add-button]', form);
    const addButtonLabel = $('[data-add-button-label]', form);
    const status = $('[data-product-status]', form);
    const price = $('[data-product-price]');
    if (!variantsPayload || !selectedVariantInput || !addButton) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsPayload.textContent);
    } catch (error) {
      console.error('Variant data could not be read', error);
      addButton.disabled = true;
      setMessage(status, 'This item is temporarily unavailable. Please refresh and try again.');
      return;
    }

    const fieldsets = $$('[data-option-index]', form);
    const optionInputs = $$('[data-option-input]', form);
    const selectedChoices = () => fieldsets.map((fieldset) => $('[data-option-input]:checked', fieldset)?.value || null);
    const selectedVariant = () => {
      const choices = selectedChoices();
      return variants.find((candidate) => candidate.options.every((value, index) => value === choices[index]));
    };
    const updateOptionAvailability = () => {
      const choices = selectedChoices();
      optionInputs.forEach((input) => {
        const fieldset = input.closest('[data-option-index]');
        const optionIndex = Number(fieldset?.dataset.optionIndex);
        const candidateChoices = [...choices];
        candidateChoices[optionIndex] = input.value;
        const selectable = variants.some((candidate) => candidate.available && candidate.options.every((value, index) => !candidateChoices[index] || candidateChoices[index] === value));
        input.disabled = !selectable;
      });
    };
    const updateSelectedLabels = () => {
      fieldsets.forEach((fieldset) => {
        const selected = $('[data-option-input]:checked', fieldset);
        const label = $('[data-option-selected-value]', fieldset);
        if (label && selected) label.textContent = `— ${selected.value}`;
      });
    };
    const syncVariant = ({ updateHistory = true } = {}) => {
      updateOptionAvailability();
      updateSelectedLabels();
      const variant = selectedVariant();
      if (!variant) {
        addButton.disabled = true;
        if (addButtonLabel) addButtonLabel.textContent = 'Unavailable';
        setMessage(status, 'This combination is unavailable.');
        return;
      }
      selectedVariantInput.value = variant.id;
      if (price) price.textContent = money(variant.price);
      addButton.disabled = !variant.available;
      if (addButtonLabel) addButtonLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
      setMessage(status, variant.available ? '' : 'This option is currently sold out.');
      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    optionInputs.forEach((input) => input.addEventListener('change', () => syncVariant()));
    syncVariant({ updateHistory: false });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (addButton.disabled || addButton.dataset.busy === 'true') return;
      const variant = selectedVariant();
      if (!variant || !variant.available) return;
      addButton.dataset.busy = 'true';
      addButton.disabled = true;
      if (addButtonLabel) addButtonLabel.textContent = 'Adding…';
      setMessage(status, 'Adding to bag…');
      const cart = await withCartMutation(() => addToCart(variant.id), status);
      addButton.dataset.busy = 'false';
      if (cart) {
        setMessage(status, `${form.dataset.productTitle || 'Item'} added to bag.`);
        syncVariant({ updateHistory: false });
      } else {
        addButton.disabled = false;
        if (addButtonLabel) addButtonLabel.textContent = 'Add to bag';
      }
    });
  });

  $('[data-search-open]')?.addEventListener('click', () => setOpen(searchOverlay, true));
  $('[data-search-close]')?.addEventListener('click', () => setOpen(searchOverlay, false));
  $('[data-menu-open]')?.addEventListener('click', () => setOpen(menuOverlay, true));
  $('[data-menu-close]')?.addEventListener('click', () => setOpen(menuOverlay, false));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(searchOverlay, false);
      setOpen(menuOverlay, false);
      setOpen(cartDrawer, false);
    }
  });

  let searchTimer;
  const searchInput = $('[data-predictive-input]');
  searchInput?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const query = searchInput.value.trim();
    const results = $('[data-predictive-results]');
    const meta = $('[data-search-meta]');
    if (!results || !meta) return;
    if (!query) {
      results.innerHTML = '';
      meta.textContent = 'Begin typing to search the collection';
      return;
    }
    searchTimer = setTimeout(async () => {
      try {
        const url = `${window.routes.predictive}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search is unavailable.');
        const markup = await response.text();
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const products = [...documentFragment.querySelectorAll('[data-predictive-product]')];
        if (products.length) {
          results.innerHTML = products.map((item) => item.outerHTML).join('');
          meta.textContent = `${products.length} objects found`;
        } else {
          results.innerHTML = '<p class="empty-catalog">No objects found.</p>';
          meta.textContent = '0 objects found';
        }
      } catch (error) {
        results.innerHTML = '<p class="empty-catalog">Search is unavailable. Press Enter to search the full catalog.</p>';
        meta.textContent = 'Search temporarily unavailable';
      }
    }, 180);
  });
})();
